import React from "react";
import { useT } from "@/lib/i18n";

// 导入本地预打包的高清案例素材
import dongchediCover from "@/assets/home/CaseMarquee/dongchedi-thumb.png";
import zdeerCover from "@/assets/home/CaseMarquee/zdeer-htumb.png";
import matrixCover from "@/assets/home/CaseMarquee/matrix-thumb.jpg";

// 案例数据定义
const CASES_DATA = [
  {
    id: "dongchedi",
    brand: "懂车帝",
    kicker: "懂车帝 · 内容量产",
    kickerEn: "dongchedi · Content Production",
    title: "单人日均内容产出提升 8×",
    titleEn: "Daily Output per Person Up 8×",
    desc: "面向汽车内容规模化生产，OranAI 整合图文、视频分镜与 AI 述品，构建可配置、可复用的内容工作流。",
    descEn: "For scalable automotive content, OranAI unifies imagery, storyboards, and AI pitching into a repeatable workflow.",
    cover: dongchediCover,
  },
  {
    id: "zdeer",
    brand: "zdeer",
    kicker: "zdeer · 电商运营",
    kickerEn: "zdeer · E-commerce Operations",
    title: "商品素材上线效率提升 3×",
    titleEn: "Material Launch Efficiency Up 3×",
    desc: "围绕智能健康与个人护理产品的线上营销需求，OranAI 将产品卖点、使用场景、目标人群与平台规范转化为统一的视觉生成体系。",
    descEn: "Targeting smart health marketing, OranAI translates selling points and audience personas into a unified visual generation system.",
    cover: zdeerCover,
  },
  {
    id: "matrix",
    brand: "Matrix",
    kicker: "Matrix · 资产管理",
    kickerEn: "Matrix · Asset Management",
    title: "累计帮助客户节省成本 300 万元+",
    titleEn: "Saved Over ¥3,000,000 in Total Cost",
    desc: "面向企业素材管理需求，OranAI 通过统一管理、智能检索与安全协同，提升数字资产使用效率。",
    descEn: "Serving enterprise asset needs, OranAI enhances digital asset utility through centralized management and smart retrieval.",
    cover: matrixCover,
  },
];

interface CaseStudiesSectionProps {
  onSelectCase?: (caseId: string) => void;
}

/**
 * S4 · CASES 案例优先板块
 * 100% 采用稳固的 Tailwind CSS 纯硬件加速样式，彻底杜绝 JS 动画引发的滚动二次跳跃
 */
export const CaseStudiesSection: React.FC<CaseStudiesSectionProps> = ({ onSelectCase }) => {
  const t = useT();

  return (
    <section id="cases" className="relative py-24 sm:py-28 px-6 overflow-hidden border-t border-[#F97316]/20 bg-[#0B0907]">
      <div className="max-w-[1400px] mx-auto">
        {/* 区域头部标题（左对齐，与素材原型 100% 一致） */}
        <div className="flex items-baseline justify-between gap-5 flex-wrap mb-3.5 text-left">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-[#F2ECE5] leading-[1.15]">
            <span>{t("From Simulation to Delivery", "从预演到触达")}</span>{" "}
            <span className="bg-gradient-to-b from-[#FDBA74] to-[#F97316] bg-clip-text text-transparent font-extrabold">
              {t("In Just Days", "只需几天")}
            </span>
          </h2>
        </div>

        {/* 区域副标题（左对齐，与素材原型 100% 一致） */}
        <p className="text-sm sm:text-base md:text-lg text-[#F2ECE5]/55 font-normal tracking-normal max-w-[640px] mb-10 sm:mb-12 text-left">
          {t(
            "See how real teams use OranAI to simulate, scale production, and distribute.",
            "看看真实团队如何用 OranAI 完成预演、量产与分发"
          )}
        </p>

        {/* 3 列响应式案例卡片网格（纯 CSS 稳定硬件加速，零跳跃零闪烁） */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
          {CASES_DATA.map((item) => (
            <article
              key={item.id}
              onClick={() => onSelectCase?.(item.id)}
              className="group relative flex flex-col rounded-[22px] overflow-hidden border border-white/[0.09] bg-[#120E0A] hover:border-[#F97316]/80 transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-[0_22px_50px_rgba(249,115,22,0.18)] cursor-pointer select-none will-change-transform"
            >
              {/* 封面素材展示区 */}
              <div className="h-[230px] w-full overflow-hidden border-b border-white/[0.09] bg-[#120E0A] flex items-center justify-center relative">
                <img
                  src={item.cover}
                  alt={`${item.brand} 案例素材`}
                  decoding="async"
                  className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                />
              </div>

              {/* 案例元信息区 */}
              <div className="p-6 sm:p-7 flex flex-col gap-3 flex-1 bg-[#120E0A]">
                {/* 业务分类标签 */}
                <span className="text-sm sm:text-[15px] font-bold text-[#FDBA74] tracking-wide">
                  {t(item.kickerEn, item.kicker)}
                </span>

                {/* 核心指标与大标题 */}
                <h3 className="text-[19px] sm:text-[21px] font-bold text-[#F2ECE5] leading-snug group-hover:text-[#FDBA74] transition-colors">
                  {t(item.titleEn, item.title)}
                </h3>

                {/* 案例说明描述 */}
                <p className="text-[14px] sm:text-[15px] text-[#F2ECE5]/75 leading-[1.65] mt-0.5">
                  {t(item.descEn, item.desc)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
